import { useRef, useState, useCallback, useEffect } from 'react';
import { logger } from '../../utils/logger';
import { Video } from 'lucide-react';

const WS_URL = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

export function WebRTCViewer({ streamId, onClose }) {
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);

  const disconnect = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const handleOffer = useCallback(async (offer) => {
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    });
    pcRef.current = pc;

    pc.ontrack = (event) => {
      if (videoRef.current) {
        videoRef.current.srcObject = event.streams[0];
      }
    };

    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice-candidate',
          data: event.candidate
        }));
      }
    };

    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'answer',
        data: answer
      }));
    }
  }, []);

  const connectToStream = useCallback(() => {
    const ws = new WebSocket(`${WS_URL}/ws/watch/${streamId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      logger.log('Viewer connected to stream');
      setConnected(true);
    };

    ws.onmessage = async (event) => {
      const message = JSON.parse(event.data);

      if (message.type === 'offer') {
        await handleOffer(message.data);
      } else if (message.type === 'ice-candidate' && message.data) {
        if (pcRef.current) {
          try {
            await pcRef.current.addIceCandidate(new RTCIceCandidate(message.data));
          } catch (e) {
            logger.error('Error adding ICE candidate:', e);
          }
        }
      }
    };

    ws.onerror = () => {
      setError('Connection error');
    };

    ws.onclose = () => {
      setConnected(false);
      setError('Stream ended');
    };
  }, [streamId, handleOffer]);

  useEffect(() => {
    connectToStream();
    return () => {
      disconnect();
    };
  }, [connectToStream, disconnect]);

  if (error) {
    return (
      <div className="aspect-video bg-black rounded-lg flex items-center justify-center">
        <div className="text-center">
          <Video className="w-12 h-12 text-gray-500 mx-auto mb-2" />
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className="w-full h-full object-cover"
      />
      {!connected && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
}
